/*!
 * Lumia — global.js (FOUNDATION)
 * Vanilla ES2019, no frameworks. Loaded with `defer` before any section script.
 *
 * Exposes window.Lumia:
 *   moneyFormat, routes, settings, strings          (bootstrapped in layout/theme.liquid)
 *   formatMoney(cents, format?)
 *   cart.get() / cart.add(items, opts) / cart.change(payload) / cart.update(updates, note?, attributes?) / cart.clear()
 *   registerCartSection(id) / cartSections()
 *   openCart()  drawer.open(id) / drawer.close(id?) / drawer.toggle(id)
 *   fetchSection(id, url?) / parseHTML(html) / replaceSection(id, html)
 *   trapFocus(container, initial?) / removeTrapFocus(elToFocus?)
 *   debounce / throttle / toast / onCartUpdated / serializeForm / updateCartCount
 *
 * Custom elements: <lh-drawer>, <lh-quantity>, <lh-tabs>, <lh-accordion>, <lh-toast>
 * Global behaviours: form[data-ajax-cart], [data-quick-add], [data-drawer-open|close|toggle],
 *                    header height observer (--lh-header-height), Esc closes drawers.
 * Events (on document unless noted): cart:updated, lumia:ready, drawer:open / drawer:close (on the drawer),
 *   ajax-cart:success / ajax-cart:error (on the form), tabs:change (on lh-tabs), accordion:open / accordion:close (on details).
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var doc = document;
  var root = doc.documentElement;
  var Lumia = (window.Lumia = window.Lumia || {});

  Lumia.version = '1.0.0';

  /* ------------------------------------------------------------------ */
  /* Config defaults (theme.liquid normally provides all of these)       */
  /* ------------------------------------------------------------------ */
  Lumia.routes = Object.assign(
    {
      root_url: '/',
      cart_url: '/cart',
      cart_add_url: '/cart/add',
      cart_change_url: '/cart/change',
      cart_update_url: '/cart/update',
      cart_clear_url: '/cart/clear',
      predictive_search_url: '/search/suggest',
      search_url: '/search'
    },
    Lumia.routes || {}
  );

  Lumia.settings = Object.assign({ cartType: 'drawer' }, Lumia.settings || {});

  Lumia.strings = Object.assign(
    {
      addToCart: 'Dodaj u korpu',
      adding: 'Dodajem…',
      added: 'Dodato u korpu',
      soldOut: 'Rasprodato',
      unavailable: 'Nedostupno',
      error: 'Došlo je do greške. Pokušajte ponovo.',
      loading: 'Učitavanje…',
      close: 'Zatvori',
      cart: 'Korpa',
      quantity: 'Količina',
      decrease: 'Smanji',
      increase: 'Povećaj',
      remove: 'Ukloni',
      continueShopping: 'Nastavi kupovinu',
      lowStock: 'Još samo [count] kom.',
      cartCount: 'Broj artikala u korpi: [count]',
      free: 'Besplatno'
    },
    Lumia.strings || {}
  );

  Lumia.moneyFormat = Lumia.moneyFormat || '{{amount_no_decimals_with_comma_separator}} RSD';

  /* ------------------------------------------------------------------ */
  /* Small utilities                                                     */
  /* ------------------------------------------------------------------ */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function throttle(fn, wait) {
    var last = 0;
    var timer = null;
    return function () {
      var ctx = this;
      var args = arguments;
      var now = Date.now();
      var remaining = wait - (now - last);
      if (remaining <= 0) {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        last = now;
        fn.apply(ctx, args);
      } else if (!timer) {
        timer = setTimeout(function () {
          last = Date.now();
          timer = null;
          fn.apply(ctx, args);
        }, remaining);
      }
    };
  }

  function emit(target, name, detail) {
    var event = new CustomEvent(name, { bubbles: true, cancelable: true, detail: detail || {} });
    (target || doc).dispatchEvent(event);
    return event;
  }

  function each(selector, fn, scope) {
    Array.prototype.forEach.call((scope || doc).querySelectorAll(selector), fn);
  }

  function interpolate(str, vars) {
    if (typeof str !== 'string') return '';
    return str.replace(/\[(\w+)\]|\{\{\s*(\w+)\s*\}\}/g, function (m, a, b) {
      var key = a || b;
      return vars && Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : m;
    });
  }

  function escapeHTML(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function safeFocus(el, preventScroll) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: !!preventScroll });
    } catch (e) {
      el.focus();
    }
  }

  Lumia.debounce = debounce;
  Lumia.throttle = throttle;
  Lumia.emit = emit;
  Lumia.interpolate = interpolate;
  Lumia.escapeHTML = escapeHTML;
  Lumia.prefersReducedMotion = prefersReducedMotion;

  /* ------------------------------------------------------------------ */
  /* Money                                                               */
  /* ------------------------------------------------------------------ */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = isNaN(precision) ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number == null) return '0';
    var negative = number < 0;
    number = (Math.abs(number) / 100.0).toFixed(precision);
    var parts = number.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var fraction = parts[1] ? decimal + parts[1] : '';
    return (negative ? '-' : '') + whole + fraction;
  }

  Lumia.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    var formatString = typeof format === 'string' && format ? format : Lumia.moneyFormat;
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholderRegex);
    var type = match ? match[1] : 'amount';
    var value = '';

    switch (type) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return match ? formatString.replace(placeholderRegex, value) : value;
  };

  /* ------------------------------------------------------------------ */
  /* Fetch helpers                                                       */
  /* ------------------------------------------------------------------ */
  function withJs(url) {
    if (!url) return url;
    var hashSplit = String(url).split('#')[0];
    var parts = hashSplit.split('?');
    if (!/\.js$/.test(parts[0])) parts[0] += '.js';
    return parts.join('?');
  }

  function extractError(json, status) {
    var msg = '';
    if (json && typeof json === 'object') {
      if (typeof json.description === 'string' && json.description) {
        msg = json.description;
      } else if (json.errors) {
        if (typeof json.errors === 'string') msg = json.errors;
        else if (Array.isArray(json.errors)) msg = json.errors.join(' ');
        else if (typeof json.errors === 'object') {
          msg = Object.keys(json.errors)
            .map(function (k) {
              var v = json.errors[k];
              return Array.isArray(v) ? v.join(' ') : String(v);
            })
            .join(' ');
        }
      } else if (typeof json.message === 'string' && json.message && json.message !== 'Cart Error') {
        msg = json.message;
      }
    }
    if (!msg) msg = Lumia.strings.error;
    var err = new Error(msg);
    err.status = status;
    err.data = json;
    return err;
  }

  function parseResponse(res) {
    return res.text().then(function (text) {
      var json = null;
      if (text) {
        try {
          json = JSON.parse(text);
        } catch (e) {
          json = null;
        }
      }
      if (!res.ok) throw extractError(json, res.status);
      if (json === null) throw extractError(null, res.status);
      return json;
    });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(parseResponse);
  }

  Lumia.fetchSection = function (sectionId, url) {
    url = url || window.location.pathname;
    var clean = String(url).split('#')[0];
    clean += (clean.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
    return fetch(clean, { credentials: 'same-origin', headers: { Accept: 'text/html' } }).then(function (res) {
      if (!res.ok) {
        var err = new Error('Section "' + sectionId + '" could not be loaded (' + res.status + ')');
        err.status = res.status;
        throw err;
      }
      return res.text();
    });
  };

  Lumia.parseHTML = function (html) {
    return new DOMParser().parseFromString(html || '', 'text/html');
  };

  Lumia.replaceSection = function (sectionId, html) {
    var target = doc.getElementById('shopify-section-' + sectionId) || doc.getElementById(sectionId);
    if (!target) return null;
    var source = null;
    if (html && typeof html === 'object' && html.nodeType) {
      source = html;
    } else {
      var parsed = Lumia.parseHTML(html);
      source = parsed.getElementById('shopify-section-' + sectionId) || parsed.getElementById(sectionId) || parsed.body;
    }
    if (!source) return target;
    // Open <lh-drawer>s inside the target re-open themselves through the pendingReopen mechanism.
    target.innerHTML = source.innerHTML;
    emit(target, 'section:replaced', { sectionId: sectionId, target: target });
    return target;
  };

  /* ------------------------------------------------------------------ */
  /* Forms                                                               */
  /* ------------------------------------------------------------------ */
  function serializeFormData(fd) {
    var obj = {};
    fd.forEach(function (value, key) {
      if (typeof File !== 'undefined' && value instanceof File) return;
      var m = /^([^\[\]]+)\[([^\]]*)\]$/.exec(key);
      if (m) {
        if (m[2] === '') {
          if (!Array.isArray(obj[m[1]])) obj[m[1]] = [];
          obj[m[1]].push(value);
        } else {
          if (!obj[m[1]] || typeof obj[m[1]] !== 'object') obj[m[1]] = {};
          obj[m[1]][m[2]] = value;
        }
      } else if (Object.prototype.hasOwnProperty.call(obj, key)) {
        obj[key] = [].concat(obj[key], value);
      } else {
        obj[key] = value;
      }
    });
    return obj;
  }

  Lumia.serializeForm = function (form) {
    if (form instanceof FormData) return serializeFormData(form);
    return serializeFormData(new FormData(form));
  };

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */
  var registeredSections = [];

  Lumia.registerCartSection = function (id) {
    if (id && registeredSections.indexOf(id) === -1) registeredSections.push(id);
    return registeredSections.slice();
  };

  Lumia.unregisterCartSection = function (id) {
    var i = registeredSections.indexOf(id);
    if (i > -1) registeredSections.splice(i, 1);
  };

  Lumia.cartSections = function () {
    var ids = registeredSections.slice();
    if (!ids.length && doc.getElementById('cart-drawer')) ids.push('cart-drawer');
    return ids;
  };

  function applySections(body, opts) {
    var ids = opts && Array.isArray(opts.sections) ? opts.sections : Lumia.cartSections();
    if (ids && ids.length) {
      body.sections = ids.join(',');
      body.sections_url = (opts && opts.sectionsUrl) || window.location.pathname;
    }
    return body;
  }

  function normalizeItem(item) {
    item = item || {};
    var out = { id: parseInt(item.id, 10) };
    var qty = item.quantity === undefined || item.quantity === null || item.quantity === '' ? 1 : parseInt(item.quantity, 10);
    out.quantity = isNaN(qty) || qty < 1 ? 1 : qty;
    if (item.properties && typeof item.properties === 'object') {
      var props = {};
      var has = false;
      Object.keys(item.properties).forEach(function (k) {
        var v = item.properties[k];
        if (v !== undefined && v !== null && String(v) !== '') {
          props[k] = v;
          has = true;
        }
      });
      if (has) out.properties = props;
    }
    if (item.selling_plan) {
      var sp = parseInt(item.selling_plan, 10);
      out.selling_plan = isNaN(sp) ? item.selling_plan : sp;
    }
    return out;
  }

  function canOpenDrawer() {
    return Lumia.settings.cartType === 'drawer' && !!Lumia.drawer.get('cart-drawer');
  }

  Lumia.cart = {
    get: function () {
      return fetch(withJs(Lumia.routes.cart_url), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      }).then(parseResponse);
    },

    add: function (items, opts) {
      opts = opts || {};
      if (typeof HTMLFormElement !== 'undefined' && items instanceof HTMLFormElement) items = Lumia.serializeForm(items);
      else if (typeof FormData !== 'undefined' && items instanceof FormData) items = serializeFormData(items);

      var list = (Array.isArray(items) ? items : [items]).map(normalizeItem);
      for (var i = 0; i < list.length; i++) {
        if (isNaN(list[i].id)) {
          var invalid = new Error(Lumia.strings.unavailable);
          invalid.status = 0;
          return Promise.reject(invalid);
        }
      }

      var body = applySections({ items: list }, opts);

      return postJSON(withJs(Lumia.routes.cart_add_url), body).then(function (json) {
        var added = Array.isArray(json.items) ? json.items : [json];
        var finish = function (cart) {
          emit(doc, 'cart:updated', {
            cart: cart,
            sections: json.sections || {},
            source: 'add',
            items: added,
            response: json
          });
          if (opts.open !== false) Lumia.openCart(opts.opener);
          return json;
        };
        return Lumia.cart.get().then(finish, function () {
          return finish(null);
        });
      });
    },

    change: function (payload, opts) {
      payload = payload || {};
      opts = opts || {};
      var body = {};
      if (payload.line !== undefined && payload.line !== null) body.line = parseInt(payload.line, 10);
      else body.id = payload.id;
      var qty = parseInt(payload.quantity, 10);
      body.quantity = isNaN(qty) || qty < 0 ? 0 : qty;
      if (payload.properties && typeof payload.properties === 'object') body.properties = payload.properties;
      if (payload.selling_plan !== undefined) body.selling_plan = payload.selling_plan;
      applySections(body, opts);

      return postJSON(withJs(Lumia.routes.cart_change_url), body).then(function (cart) {
        emit(doc, 'cart:updated', { cart: cart, sections: cart.sections || {}, source: 'change', response: cart, payload: payload });
        return cart;
      });
    },

    update: function (updates, note, attributes, opts) {
      opts = opts || {};
      var body = {};
      if (updates && typeof updates === 'object') body.updates = updates;
      if (note !== undefined && note !== null) body.note = note;
      if (attributes && typeof attributes === 'object') body.attributes = attributes;
      applySections(body, opts);

      return postJSON(withJs(Lumia.routes.cart_update_url), body).then(function (cart) {
        emit(doc, 'cart:updated', { cart: cart, sections: cart.sections || {}, source: 'update', response: cart });
        return cart;
      });
    },

    clear: function (opts) {
      opts = opts || {};
      var body = applySections({}, opts);
      return postJSON(withJs(Lumia.routes.cart_clear_url || '/cart/clear'), body).then(function (cart) {
        emit(doc, 'cart:updated', { cart: cart, sections: cart.sections || {}, source: 'clear', response: cart });
        return cart;
      });
    }
  };

  Lumia.openCart = function (opener) {
    if (canOpenDrawer()) {
      Lumia.drawer.open('cart-drawer', opener);
      return true;
    }
    window.location.href = Lumia.routes.cart_url;
    return false;
  };

  Lumia.updateCartCount = function (cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    var count = cart.item_count;
    each('[data-cart-count]', function (el) {
      el.textContent = String(count);
      el.classList.toggle('is-empty', count === 0);
      el.setAttribute('data-count', String(count));
    });
    each('[data-cart-count-label]', function (el) {
      el.textContent = interpolate(Lumia.strings.cartCount, { count: count });
    });
    each('[data-cart-total]', function (el) {
      el.textContent = Lumia.formatMoney(cart.total_price);
    });
    each('[data-cart-empty-toggle]', function (el) {
      el.classList.toggle('is-empty', count === 0);
    });
  };

  Lumia.onCartUpdated = function (fn) {
    if (typeof fn !== 'function') return function () {};
    var handler = function (e) {
      fn(e.detail, e);
    };
    doc.addEventListener('cart:updated', handler);
    return function () {
      doc.removeEventListener('cart:updated', handler);
    };
  };

  doc.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.cart) Lumia.updateCartCount(e.detail.cart);
  });

  /* ------------------------------------------------------------------ */
  /* Focus trap                                                          */
  /* ------------------------------------------------------------------ */
  var FOCUSABLE =
    'summary, a[href], area[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, object, embed, audio[controls], video[controls], ' +
    '[contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex^="-"])';

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function getFocusable(container) {
    if (!container) return [];
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return isVisible(el) && !el.closest('[hidden]') && !el.closest('[inert]') && !el.closest('[aria-hidden="true"]:not(.lh-drawer)');
    });
  }

  var trap = null;

  Lumia.getFocusable = getFocusable;

  Lumia.trapFocus = function (container, initialFocus) {
    Lumia.removeTrapFocus();
    if (!container) return;

    var focusin = function (e) {
      if (e.target === doc || e.target === container || container.contains(e.target)) return;
      var first = getFocusable(container)[0];
      safeFocus(first || container, true);
    };
    var keydown = function (e) {
      if (e.key !== 'Tab') return;
      var els = getFocusable(container);
      if (!els.length) {
        e.preventDefault();
        safeFocus(container, true);
        return;
      }
      var first = els[0];
      var last = els[els.length - 1];
      var active = doc.activeElement;
      if (e.shiftKey && (active === first || active === container || !container.contains(active))) {
        e.preventDefault();
        safeFocus(last, true);
      } else if (!e.shiftKey && (active === last || !container.contains(active))) {
        e.preventDefault();
        safeFocus(first, true);
      }
    };

    trap = { container: container, focusin: focusin, keydown: keydown };
    doc.addEventListener('focusin', focusin);
    doc.addEventListener('keydown', keydown);

    var target = initialFocus || getFocusable(container)[0] || container;
    if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    safeFocus(target, true);
  };

  Lumia.removeTrapFocus = function (elToFocus) {
    if (trap) {
      doc.removeEventListener('focusin', trap.focusin);
      doc.removeEventListener('keydown', trap.keydown);
      trap = null;
    }
    if (elToFocus) safeFocus(elToFocus, true);
  };

  /* ------------------------------------------------------------------ */
  /* Scroll lock                                                         */
  /* ------------------------------------------------------------------ */
  function lockScroll() {
    if (doc.body.classList.contains('lh-scroll-lock')) return;
    var sbw = window.innerWidth - root.clientWidth;
    if (sbw > 0) doc.body.style.setProperty('--lh-scrollbar-width', sbw + 'px');
    doc.body.classList.add('lh-scroll-lock');
  }

  function unlockScroll() {
    doc.body.classList.remove('lh-scroll-lock');
    doc.body.style.removeProperty('--lh-scrollbar-width');
  }

  /* ------------------------------------------------------------------ */
  /* <lh-drawer>                                                         */
  /* ------------------------------------------------------------------ */
  var drawerStack = []; // open drawers, bottom → top
  var pendingReopen = {}; // id → { opener, focusSelector } while a section re-render swaps the element

  function setTriggersExpanded(id, expanded) {
    if (!id) return;
    var sel = '[data-drawer-open="' + id + '"], [data-drawer-toggle="' + id + '"]';
    each(sel, function (el) {
      el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function focusSelectorFor(el) {
    if (!el || el === doc.body) return null;
    if (el.id) return '#' + (window.CSS && CSS.escape ? CSS.escape(el.id) : el.id);
    var key = el.getAttribute('data-focus-key');
    if (key) return '[data-focus-key="' + key + '"]';
    if (el.name) return el.tagName.toLowerCase() + '[name="' + el.name + '"]';
    return null;
  }

  class LhDrawer extends HTMLElement {
    constructor() {
      super();
      this._opener = null;
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this.classList.add('lh-drawer');
      if (this.getAttribute('data-position') === 'left') this.classList.add('lh-drawer--left');

      this.panel = this.querySelector('.lh-drawer__panel') || this.firstElementChild || this;
      if (this.panel !== this) {
        if (!this.panel.hasAttribute('role')) this.panel.setAttribute('role', 'dialog');
        if (!this.panel.hasAttribute('aria-modal')) this.panel.setAttribute('aria-modal', 'true');
        if (!this.panel.hasAttribute('tabindex')) this.panel.setAttribute('tabindex', '-1');
        var title = this.querySelector('.lh-drawer__title');
        if (title && !this.panel.hasAttribute('aria-labelledby')) {
          if (!title.id) title.id = (this.id || 'lh-drawer') + '-title';
          this.panel.setAttribute('aria-labelledby', title.id);
        }
      }

      this.addEventListener('click', this._onClick);

      var reopen = this.id && pendingReopen[this.id];
      if (reopen) {
        delete pendingReopen[this.id];
        this.classList.remove('is-open');
        this.open(reopen.opener, { instant: true, focusSelector: reopen.focusSelector });
      } else if (this.classList.contains('is-open')) {
        this.classList.remove('is-open');
        this.open(null, { instant: true });
      } else {
        this.setAttribute('aria-hidden', 'true');
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (!this.classList.contains('is-open')) return;

      var id = this.id;
      var opener = this._opener;
      var self = this;
      var active = doc.activeElement;
      var focusSelector = active && this.contains(active) ? focusSelectorFor(active) : null;

      var idx = drawerStack.indexOf(this);
      if (idx > -1) drawerStack.splice(idx, 1);
      setTriggersExpanded(id, false);
      this.classList.remove('is-open');

      if (!id) {
        this._afterClose(opener, true);
        return;
      }

      // Section re-render: a replacement with the same id may already be connected
      // (browsers run the new element's connectedCallback before this callback).
      var replacement = doc.getElementById(id);
      if (replacement && replacement !== this && typeof replacement.open === 'function' && !replacement.isOpen) {
        replacement.open(opener, { instant: true, focusSelector: focusSelector });
        return;
      }

      // Otherwise wait a tick for a replacement to connect; if none does, finish closing.
      pendingReopen[id] = { opener: opener, focusSelector: focusSelector };
      setTimeout(function () {
        if (pendingReopen[id]) {
          delete pendingReopen[id];
          self._afterClose(opener, true);
        }
      }, 80);
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener, opts) {
      opts = opts || {};
      if (this.isOpen) return;

      if (opener && opener.nodeType === 1) this._opener = opener;
      else if (!opts.instant) this._opener = doc.activeElement && doc.activeElement !== doc.body ? doc.activeElement : null;

      if (drawerStack.indexOf(this) === -1) drawerStack.push(this);
      if (drawerStack.length > 1) this.style.zIndex = String(60 + drawerStack.length - 1);

      if (opts.instant) this.classList.add('lh-drawer--instant');
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      lockScroll();
      setTriggersExpanded(this.id, true);

      var self = this;
      var focusTarget =
        (opts.focus && opts.focus.nodeType === 1 ? opts.focus : null) ||
        (opts.focusSelector ? this.querySelector(opts.focusSelector) : null) ||
        this.querySelector('[data-drawer-focus]') ||
        this.querySelector('.lh-drawer__close') ||
        null;

      requestAnimationFrame(function () {
        if (!self.isOpen) return;
        Lumia.trapFocus(self.panel || self, focusTarget);
        if (opts.instant) {
          requestAnimationFrame(function () {
            self.classList.remove('lh-drawer--instant');
          });
        }
      });

      emit(this, 'drawer:open', { drawer: this, opener: this._opener, instant: !!opts.instant });
    }

    close(opts) {
      opts = opts || {};
      if (!this.isOpen) return;

      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      var idx = drawerStack.indexOf(this);
      if (idx > -1) drawerStack.splice(idx, 1);
      this.style.zIndex = '';
      setTriggersExpanded(this.id, false);

      var opener = this._opener;
      this._opener = null;
      this._afterClose(opener, opts.returnFocus !== false);

      emit(this, 'drawer:close', { drawer: this });
    }

    _afterClose(opener, returnFocus) {
      var openerOk = returnFocus && opener && doc.body.contains(opener) ? opener : null;
      if (!drawerStack.length) {
        unlockScroll();
        Lumia.removeTrapFocus(openerOk);
      } else {
        var top = drawerStack[drawerStack.length - 1];
        Lumia.trapFocus(top.panel || top, openerOk && top.contains(openerOk) ? openerOk : null);
      }
    }

    toggle(opener, opts) {
      if (this.isOpen) this.close(opts);
      else this.open(opener, opts);
    }

    _onClick(e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      if (t === this || t.classList.contains('lh-drawer__overlay')) {
        e.preventDefault();
        this.close();
      }
    }
  }

  Lumia.drawer = {
    get: function (idOrEl) {
      var el = typeof idOrEl === 'string' ? doc.getElementById(idOrEl) : idOrEl;
      if (!el || !(el instanceof Element)) return null;
      if (el.tagName.toLowerCase() !== 'lh-drawer') {
        el = el.closest('lh-drawer') || el.querySelector('lh-drawer');
      }
      return el && typeof el.open === 'function' ? el : null;
    },
    open: function (id, opener, opts) {
      var el = this.get(id);
      if (el) el.open(opener, opts);
      return el;
    },
    close: function (id, opts) {
      var el = id ? this.get(id) : drawerStack[drawerStack.length - 1];
      if (el) el.close(opts);
      return el;
    },
    closeAll: function () {
      drawerStack.slice().reverse().forEach(function (d) {
        d.close({ returnFocus: false });
      });
    },
    toggle: function (id, opener, opts) {
      var el = this.get(id);
      if (el) el.toggle(opener, opts);
      return el;
    },
    isOpen: function (id) {
      var el = this.get(id);
      return !!(el && el.isOpen);
    },
    stack: function () {
      return drawerStack.slice();
    }
  };

  /* ------------------------------------------------------------------ */
  /* <lh-quantity>                                                       */
  /* ------------------------------------------------------------------ */
  class LhQuantity extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onChange = this._onChange.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="number"]') || this.querySelector('input');
      if (!this.input) return;
      this.minus = this.querySelector('[data-qty-minus]');
      this.plus = this.querySelector('[data-qty-plus]');
      this.addEventListener('click', this._onClick);
      this.input.addEventListener('change', this._onChange);
      this.validate();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this.input) this.input.removeEventListener('change', this._onChange);
    }

    get min() {
      var v = parseInt(this.input.getAttribute('min'), 10);
      return isNaN(v) ? 1 : v;
    }

    get max() {
      var v = parseInt(this.input.getAttribute('max'), 10);
      return isNaN(v) ? Infinity : v;
    }

    get step() {
      var v = parseInt(this.input.getAttribute('step'), 10);
      return isNaN(v) || v < 1 ? 1 : v;
    }

    get value() {
      var v = parseInt(this.input.value, 10);
      return isNaN(v) ? this.min : v;
    }

    set value(v) {
      this.setValue(v, false);
    }

    clamp(v) {
      v = parseInt(v, 10);
      if (isNaN(v)) v = this.min;
      if (v < this.min) v = this.min;
      if (v > this.max) v = this.max;
      return v;
    }

    setValue(v, emitChange) {
      var next = this.clamp(v);
      if (String(next) !== this.input.value) {
        this.input.value = String(next);
        if (emitChange) {
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.validate();
    }

    stepBy(direction) {
      this.setValue(this.value + direction * this.step, true);
    }

    validate() {
      if (!this.input) return;
      var v = this.value;
      if (this.minus) this.minus.disabled = v <= this.min;
      if (this.plus) this.plus.disabled = this.max !== Infinity && v >= this.max;
    }

    _onClick(e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var minus = t.closest('[data-qty-minus]');
      var plus = t.closest('[data-qty-plus]');
      if (minus && this.contains(minus)) {
        e.preventDefault();
        this.stepBy(-1);
      } else if (plus && this.contains(plus)) {
        e.preventDefault();
        this.stepBy(1);
      }
    }

    _onChange() {
      var next = this.clamp(this.input.value);
      if (String(next) !== this.input.value) this.input.value = String(next);
      this.validate();
    }
  }

  /* ------------------------------------------------------------------ */
  /* <lh-tabs>                                                           */
  /* ------------------------------------------------------------------ */
  var tabsCounter = 0;

  class LhTabs extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this.index = -1;
    }

    connectedCallback() {
      var self = this;
      this.tabs = Array.prototype.filter.call(this.querySelectorAll('[role="tab"]'), function (t) {
        return t.closest('lh-tabs') === self;
      });
      this.panels = Array.prototype.filter.call(this.querySelectorAll('[role="tabpanel"]'), function (p) {
        return p.closest('lh-tabs') === self;
      });
      if (!this.tabs.length) return;

      var uid = this.id || 'lh-tabs-' + ++tabsCounter;
      this.tabs.forEach(function (tab, i) {
        if (!tab.id) tab.id = uid + '-tab-' + (i + 1);
        if (tab.tagName === 'BUTTON' && !tab.hasAttribute('type')) tab.setAttribute('type', 'button');
        var panel = self.panelFor(tab, i);
        if (panel) {
          if (!panel.id) panel.id = uid + '-panel-' + (i + 1);
          tab.setAttribute('aria-controls', panel.id);
          panel.setAttribute('aria-labelledby', tab.id);
          if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '0');
        }
      });

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);

      var initial = -1;
      if (this.hasAttribute('data-hash') && window.location.hash.length > 1) {
        var hash = decodeURIComponent(window.location.hash.slice(1));
        initial = this.tabs.findIndex(function (t) {
          return (t.getAttribute('data-hash') || '') === hash || t.id === hash || t.getAttribute('aria-controls') === hash;
        });
      }
      if (initial < 0) {
        initial = this.tabs.findIndex(function (t) {
          return t.getAttribute('aria-selected') === 'true';
        });
      }
      if (initial < 0) initial = 0;
      this.select(initial, { focus: false, updateHash: false });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
    }

    panelFor(tab, i) {
      var id = tab.getAttribute('aria-controls');
      var byId = id ? doc.getElementById(id) : null;
      return byId || this.panels[i] || null;
    }

    select(index, opts) {
      opts = opts || {};
      if (!this.tabs || !this.tabs.length) return;
      index = Math.max(0, Math.min(index, this.tabs.length - 1));
      var self = this;
      this.tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        tab.classList.toggle('is-active', selected);
        var panel = self.panelFor(tab, i);
        if (panel) panel.hidden = !selected;
      });
      var changed = this.index !== index;
      this.index = index;
      var tab = this.tabs[index];
      if (opts.focus !== false) safeFocus(tab, true);
      if (opts.updateHash !== false && this.hasAttribute('data-hash') && window.history && window.history.replaceState) {
        var hash = tab.getAttribute('data-hash') || tab.getAttribute('aria-controls') || tab.id;
        if (hash) window.history.replaceState(null, '', '#' + hash);
      }
      if (changed) emit(this, 'tabs:change', { index: index, tab: tab, panel: this.panelFor(tab, index) });
    }

    _onClick(e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var tab = t.closest('[role="tab"]');
      if (!tab) return;
      var i = this.tabs.indexOf(tab);
      if (i < 0) return;
      e.preventDefault();
      this.select(i, { focus: false });
    }

    _onKeydown(e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var tab = t.closest('[role="tab"]');
      if (!tab) return;
      var i = this.tabs.indexOf(tab);
      if (i < 0) return;
      var n = this.tabs.length;
      var next;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (i + 1) % n;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (i - 1 + n) % n;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = n - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      this.select(next);
    }
  }

  /* ------------------------------------------------------------------ */
  /* <lh-accordion>                                                      */
  /* ------------------------------------------------------------------ */
  function accordionContent(details) {
    var summary = details.querySelector(':scope > summary') || details.querySelector('summary');
    var content = details.querySelector(':scope > .lh-accordion__content');
    if (!content && summary) content = summary.nextElementSibling;
    return content || null;
  }

  class LhAccordion extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
    }

    get single() {
      return this.getAttribute('data-single') === 'true';
    }

    items() {
      var self = this;
      return Array.prototype.filter.call(this.querySelectorAll('details'), function (d) {
        return d.closest('lh-accordion') === self;
      });
    }

    _onClick(e) {
      var t = e.target;
      if (!(t instanceof Element)) return;
      var summary = t.closest('summary');
      if (!summary || !this.contains(summary)) return;
      var details = summary.parentElement;
      if (!details || details.tagName !== 'DETAILS' || details.closest('lh-accordion') !== this) return;
      var inner = t.closest('a, button, input, select, textarea, label');
      if (inner && inner !== summary && summary.contains(inner)) return;

      e.preventDefault();
      if (details.open && !details.classList.contains('is-closing')) {
        this.closeItem(details);
      } else {
        if (this.single) {
          var self = this;
          this.items().forEach(function (d) {
            if (d !== details && d.open) self.closeItem(d);
          });
        }
        this.openItem(details);
      }
    }

    openItem(details) {
      var content = accordionContent(details);
      if (details._lhAnim) {
        details._lhAnim.cancel();
        details._lhAnim = null;
      }
      details.classList.remove('is-closing');
      if (details.open) return;
      details.open = true;
      emit(details, 'accordion:open', { details: details, accordion: this });

      if (!content || prefersReducedMotion() || typeof content.animate !== 'function') return;

      var cs = window.getComputedStyle(content);
      var height = content.getBoundingClientRect().height;
      content.style.overflow = 'hidden';
      var anim = content.animate(
        [
          { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
          { height: height + 'px', paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, opacity: 1 }
        ],
        { duration: 260, easing: 'ease' }
      );
      details._lhAnim = anim;
      var done = function () {
        content.style.overflow = '';
        if (details._lhAnim === anim) details._lhAnim = null;
      };
      anim.onfinish = done;
      anim.oncancel = done;
    }

    closeItem(details) {
      var content = accordionContent(details);
      if (details._lhAnim) {
        details._lhAnim.cancel();
        details._lhAnim = null;
      }
      if (!details.open) return;
      var self = this;

      if (!content || prefersReducedMotion() || typeof content.animate !== 'function') {
        details.open = false;
        emit(details, 'accordion:close', { details: details, accordion: self });
        return;
      }

      var cs = window.getComputedStyle(content);
      var height = content.getBoundingClientRect().height;
      content.style.overflow = 'hidden';
      details.classList.add('is-closing');
      var anim = content.animate(
        [
          { height: height + 'px', paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom, opacity: 1 },
          { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 }
        ],
        { duration: 220, easing: 'ease' }
      );
      details._lhAnim = anim;
      anim.onfinish = function () {
        details.open = false;
        details.classList.remove('is-closing');
        content.style.overflow = '';
        if (details._lhAnim === anim) details._lhAnim = null;
        emit(details, 'accordion:close', { details: details, accordion: self });
      };
      anim.oncancel = function () {
        details.classList.remove('is-closing');
        content.style.overflow = '';
        if (details._lhAnim === anim) details._lhAnim = null;
      };
    }
  }

  /* ------------------------------------------------------------------ */
  /* <lh-toast>                                                          */
  /* ------------------------------------------------------------------ */
  class LhToast extends HTMLElement {
    connectedCallback() {
      this.classList.add('lh-toast');
      if (!this.hasAttribute('role')) this.setAttribute('role', 'status');
      if (!this.hasAttribute('aria-live')) this.setAttribute('aria-live', 'polite');
      this.setAttribute('aria-atomic', 'true');
    }

    show(message, opts) {
      opts = opts || {};
      var self = this;
      clearTimeout(this._timer);
      this.classList.remove('lh-toast--error', 'lh-toast--success', 'lh-toast--info');
      if (opts.type) this.classList.add('lh-toast--' + opts.type);
      this.textContent = '';
      requestAnimationFrame(function () {
        self.textContent = String(message);
        self.classList.add('is-visible');
      });
      var duration = opts.duration === undefined ? 3200 : Number(opts.duration);
      if (duration > 0) {
        this._timer = setTimeout(function () {
          self.hide();
        }, duration);
      }
      return this;
    }

    hide() {
      clearTimeout(this._timer);
      this.classList.remove('is-visible');
      return this;
    }
  }

  Lumia.toast = function (message, opts) {
    if (!message) return null;
    var el = doc.querySelector('lh-toast');
    if (!el) {
      el = doc.createElement('lh-toast');
      doc.body.appendChild(el);
    }
    if (typeof el.show === 'function') el.show(message, opts);
    else el.textContent = String(message);
    return el;
  };

  /* ------------------------------------------------------------------ */
  /* Define custom elements (guarded, so re-including the script is safe)*/
  /* ------------------------------------------------------------------ */
  function define(name, ctor) {
    if (window.customElements && !customElements.get(name)) customElements.define(name, ctor);
  }
  define('lh-drawer', LhDrawer);
  define('lh-quantity', LhQuantity);
  define('lh-tabs', LhTabs);
  define('lh-accordion', LhAccordion);
  define('lh-toast', LhToast);

  Lumia.elements = { LhDrawer: LhDrawer, LhQuantity: LhQuantity, LhTabs: LhTabs, LhAccordion: LhAccordion, LhToast: LhToast };

  /* ------------------------------------------------------------------ */
  /* Loading state helper                                                */
  /* ------------------------------------------------------------------ */
  function setLoading(btn, state) {
    if (!btn) return;
    btn.classList.toggle('is-loading', !!state);
    if (state) {
      btn.setAttribute('aria-busy', 'true');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-disabled');
    }
  }
  Lumia.setLoading = setLoading;

  /* ------------------------------------------------------------------ */
  /* Global delegated behaviours                                         */
  /* ------------------------------------------------------------------ */
  function quickAdd(btn, e) {
    var id = btn.getAttribute('data-quick-add');
    if (!id) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;
    var qty = parseInt(btn.getAttribute('data-quantity'), 10) || 1;
    setLoading(btn, true);
    Lumia.cart
      .add({ id: id, quantity: qty }, { open: true, opener: btn })
      .then(function (json) {
        emit(btn, 'quick-add:success', { response: json, button: btn });
      })
      .catch(function (err) {
        Lumia.toast((err && err.message) || Lumia.strings.error, { type: 'error' });
        emit(btn, 'quick-add:error', { error: err, button: btn });
      })
      .then(function () {
        setLoading(btn, false);
      });
  }

  doc.addEventListener('click', function (e) {
    var t = e.target;
    if (!(t instanceof Element)) return;
    var el;

    if ((el = t.closest('[data-drawer-open]'))) {
      var openId = el.getAttribute('data-drawer-open');
      if (openId && Lumia.drawer.get(openId)) {
        e.preventDefault();
        Lumia.drawer.open(openId, el);
      }
      return;
    }
    if ((el = t.closest('[data-drawer-toggle]'))) {
      var toggleId = el.getAttribute('data-drawer-toggle');
      if (toggleId && Lumia.drawer.get(toggleId)) {
        e.preventDefault();
        Lumia.drawer.toggle(toggleId, el);
      }
      return;
    }
    if ((el = t.closest('[data-drawer-close]'))) {
      var closeId = el.getAttribute('data-drawer-close');
      var drawer = (closeId && Lumia.drawer.get(closeId)) || el.closest('lh-drawer');
      if (drawer && typeof drawer.close === 'function') {
        e.preventDefault();
        drawer.close();
      }
      return;
    }
    if ((el = t.closest('[data-quick-add]'))) {
      quickAdd(el, e);
    }
  });

  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    if (!drawerStack.length) return;
    var top = drawerStack[drawerStack.length - 1];
    if (top && top.isOpen) {
      e.preventDefault();
      top.close();
    }
  });

  doc.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-ajax-cart')) return;
    if (!window.fetch || !window.FormData) return; // let the browser submit natively

    e.preventDefault();
    if (form.classList.contains('is-submitting')) return;

    var mode = form.getAttribute('data-ajax-cart') || '';
    var submitter =
      e.submitter || form.querySelector('button[type="submit"]:not([disabled]), input[type="submit"]:not([disabled]), button:not([type])');
    var errorEl = form.querySelector('[data-form-error]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    form.classList.add('is-submitting');
    setLoading(submitter, true);

    var useToast = mode === 'toast' && !canOpenDrawer();
    var data = Lumia.serializeForm(form);

    Lumia.cart
      .add(data, { open: !useToast, opener: submitter })
      .then(function (json) {
        if (useToast) Lumia.toast(Lumia.strings.added, { type: 'success' });
        emit(form, 'ajax-cart:success', { response: json, form: form });
      })
      .catch(function (err) {
        var msg = (err && err.message) || Lumia.strings.error;
        if (errorEl) {
          errorEl.textContent = msg;
          errorEl.hidden = false;
          if (!errorEl.hasAttribute('role')) errorEl.setAttribute('role', 'alert');
        } else {
          Lumia.toast(msg, { type: 'error' });
        }
        emit(form, 'ajax-cart:error', { error: err, form: form });
      })
      .then(function () {
        form.classList.remove('is-submitting');
        setLoading(submitter, false);
      });
  });

  /* ------------------------------------------------------------------ */
  /* Header height → --lh-header-height                                  */
  /* ------------------------------------------------------------------ */
  var headerObserver = null;
  var headerEl = null;
  var lastHeaderH = 0;

  // hArg: izmerena visina iz ResizeObserver-a (borderBoxSize). Bez nje merimo getBoundingClientRect
  // samo kao fallback (resize bez RO). Upis na :root ide samo kad se vrednost promeni — nasleđena
  // custom property na <html> inače izaziva style recalc celog stabla.
  function setHeaderHeight(hArg) {
    var h;
    if (typeof hArg === 'number' && isFinite(hArg)) {
      h = hArg;
    } else {
      if (!headerEl || !doc.body.contains(headerEl)) headerEl = doc.querySelector('.lh-header');
      if (!headerEl) return;
      h = headerEl.getBoundingClientRect().height;
    }
    h = Math.round(h);
    if (h > 0 && h !== lastHeaderH) {
      lastHeaderH = h;
      root.style.setProperty('--lh-header-height', h + 'px');
    }
  }

  function observeHeader() {
    var header = doc.querySelector('.lh-header');
    if (headerObserver) {
      headerObserver.disconnect();
      headerObserver = null;
    }
    headerEl = header;
    if (!header) return;
    if ('ResizeObserver' in window) {
      // RO sam isporučuje početno merenje posle layouta (čisto čitanje) —
      // nema sinhronog getBoundingClientRect u DOMContentLoaded tasku.
      headerObserver = new ResizeObserver(function (entries) {
        var e = entries[entries.length - 1];
        var b = e && e.borderBoxSize;
        if (b && !('length' in b)) b = [b]; // stariji Firefox: objekat umesto niza
        var box = b && b[0];
        setHeaderHeight(box && box.blockSize ? box.blockSize : undefined);
      });
      headerObserver.observe(header);
    } else {
      window.requestAnimationFrame(function () { setHeaderHeight(); });
      window.addEventListener('load', function () { setHeaderHeight(); }, { once: true });
    }
  }

  // Omotač je obavezan: debounce prosleđuje Event kao prvi argument, a on ne sme postati hArg.
  window.addEventListener('resize', debounce(function () { setHeaderHeight(); }, 120));

  /* ------------------------------------------------------------------ */
  /* Theme editor hooks                                                  */
  /* ------------------------------------------------------------------ */
  doc.addEventListener('shopify:section:load', function () {
    observeHeader();
  });
  doc.addEventListener('shopify:section:unload', function () {
    observeHeader();
  });
  /* Only drawers that opt in with [data-editor-open] (the cart drawer) are
     previewed when their section is selected; header/facets/password drawers
     must stay closed. */
  function editorDrawer(section) {
    if (!(section instanceof Element)) return null;
    var d = section.querySelector('lh-drawer[data-editor-open]');
    if (!d && section.id === 'shopify-section-cart-drawer') d = section.querySelector('lh-drawer#cart-drawer');
    return d;
  }
  doc.addEventListener('shopify:section:select', function (e) {
    var d = editorDrawer(e.target);
    if (d && typeof d.open === 'function' && !d.isOpen) d.open(null, { instant: false });
  });
  doc.addEventListener('shopify:section:deselect', function (e) {
    var d = editorDrawer(e.target);
    if (d && typeof d.close === 'function' && d.isOpen) d.close({ returnFocus: false });
  });
  /* Reveal collapsed accordion items / hidden tab panels when their block is
     selected in the editor sidebar. */
  doc.addEventListener('shopify:block:select', function (e) {
    var el = e.target;
    if (!(el instanceof Element)) return;
    var details = el.closest('details');
    if (details) {
      var acc = details.closest('lh-accordion');
      if (acc && typeof acc.openItem === 'function') {
        if (acc.single) {
          acc.items().forEach(function (d) {
            if (d !== details && d.open) acc.closeItem(d);
          });
        }
        acc.openItem(details);
      } else {
        details.open = true;
      }
      return;
    }
    var tab = el.closest('[role="tab"]');
    var tabs = tab && tab.closest('lh-tabs');
    if (tabs && tabs.tabs && typeof tabs.select === 'function') {
      var i = tabs.tabs.indexOf(tab);
      if (i >= 0) tabs.select(i, { focus: false, updateHash: false });
    }
  });

  /* ------------------------------------------------------------------ */
  /* Ready                                                               */
  /* ------------------------------------------------------------------ */
  var readyQueue = [];
  Lumia.isReady = false;
  Lumia.ready = function (fn) {
    if (typeof fn !== 'function') return;
    if (Lumia.isReady) fn(Lumia);
    else readyQueue.push(fn);
  };

  function boot() {
    observeHeader();
    Lumia.isReady = true;
    readyQueue.splice(0).forEach(function (fn) {
      try {
        fn(Lumia);
      } catch (err) {
        if (window.console) console.error(err);
      }
    });
    emit(doc, 'lumia:ready', { Lumia: Lumia });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
